import { faArrowRight, faListAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Button, Card, Col, Collapse, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { storesContext } from '../../../stores/storesContext';
import { stages } from '../../crop_scanning/CropScanningUtils';

const PropertiesReports = ({ userReports, userBlocks }) => {
  const userStore = useContext(storesContext);
  const propertiesStore = userStore.propertiesStore;
  const [showResults, setShowResults] = useState(true);
  const history = useHistory();

  useEffect(() => {
    if (localStorage.getItem('showDashboardResults'))
      setShowResults(
        localStorage.getItem('showDashboardResults') === 'true' ? true : false
      );
  }, []);

  const handleClick = () => {
    localStorage.setItem('showDashboardResults', !showResults);
    setShowResults(
      localStorage.getItem('showDashboardResults') === 'true' ? true : false
    );
  };

  const propertiesReports = useMemo(
    () =>
      userReports
        .filter(
          (propertyReports) =>
            !(
              propertyReports.reports.length === 0 &&
              propertyReports.total_reports === 0
            )
        )
        .map((propertyReports) => {
          const propertyId = propertyReports.property_id;
          const property = propertiesStore.properties.find(
            (p) => p.id === propertyId
          );
          propertyReports.property = property;
          return propertyReports;
        })
        // Filter out property reports that don't have a property.
        .filter((propertyReports) => propertyReports.property)
        // Filter out property reports where a report block does not appear in the users blocks.
        .filter((propertyReports) => {
          const reports = propertyReports.reports;
          if (reports.length === 0) return true;
          const blockNames = reports.map((report) =>
            report.Key.split('_')[2].replace(/\s/g, '')
          );
          const property_id = propertyReports.property_id;
          const blockClusters =
            userBlocks.find(
              (userBlock) => userBlock.property_id === property_id
            )?.block_clusters || [];
          return blockClusters.some((cluster) => {
            const primaryGaBlock = cluster.primary_ga_block;
            const feature = JSON.parse(primaryGaBlock.geojson_feature);
            const blockName = feature.properties.details.block.replace(
              /\s/g,
              ''
            );
            return blockNames.includes(blockName);
          });
        })
        // Sort by whether the property has latest reports.
        .sort((a, b) => {
          const aHasLatestReports = a.reports.length > 0;
          const bHasLatestReports = b.reports.length > 0;
          if (aHasLatestReports && !bHasLatestReports) return -1;
          if (!aHasLatestReports && bHasLatestReports) return 1;
          return 0;
        }),
    [propertiesStore.properties, userBlocks, userReports]
  );

  return (
    <Card className='mt-2'>
      <Card.Header onClick={handleClick} className='text-center'>
        {showResults ? (
          <Fragment>
            Precision Hort Results
            <br />
          </Fragment>
        ) : null}
        <small>
          <FontAwesomeIcon icon={faListAlt} /> {showResults ? 'Hide' : 'Show'}{' '}
          Results
        </small>
      </Card.Header>
      <Collapse in={showResults}>
        <div>
          {propertiesReports.map((propertyReports) => {
            const property = propertyReports.property;
            const reports = propertyReports.reports;
            const hasLastestReports = reports.length > 0;
            return (
              <Row
                key={propertyReports.property_id}
                className='my-2 mx-1 p-0'
                noGutters
              >
                <Col xs={12} className='bg-light'>
                  <Row noGutters style={{ minHeight: '65px' }}>
                    <Col
                      xs={1}
                      className={
                        hasLastestReports ? 'bg-primary' : 'bg-secondary'
                      }
                    />
                    <Col xs={8} className='py-2 pl-3 pr-0'>
                      <Row>
                        <Col>
                          <b>{property.name}</b>
                        </Col>
                      </Row>
                      {reports.map((report) => {
                        const reportKey = report.Key;
                        const parts = reportKey.split('_');
                        const blockName = parts[2];
                        const stage = parts[4];
                        const stageName = stages.find(
                          (s) => s.value === stage
                        )?.label;
                        const date = moment(parts[7]).format('DD/MM/YYYY');
                        return (
                          <Row key={reportKey}>
                            <Col>{`${blockName}: ${stageName} report - ${date}`}</Col>
                          </Row>
                        );
                      })}
                    </Col>
                    <Col
                      xs={3}
                      className='d-flex align-items-center justify-content-end pr-3'
                    >
                      <Button
                        variant='outline-primary'
                        className='float-right'
                        onClick={() => {
                          const blockName =
                            reports.length > 0
                              ? reports[0].Key.split('_')[2]
                              : undefined;
                          const url = '/precision-hort/block-scanning-reports';
                          history.push(
                            blockName ? `${url}?block=${blockName}` : url
                          );
                          window.scroll(0, 0);
                          propertiesStore.setSelectedProperty(property.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            );
          })}
        </div>
      </Collapse>
    </Card>
  );
};

PropertiesReports.propTypes = {
  userReports: PropTypes.array.isRequired
};

export default observer(PropertiesReports);

